import {
  Button,
  Card,
  IndicatorLabel,
  KebabIcon,
  Menu,
  Popover,
  Tooltip,
  TooltipContent,
  Typography,
} from "@codat/orchard-ui";
import { Product } from "api/schema/productsSchema";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { getProductDetails } from "utils";

import styles from "./ProductCard.module.scss";

export interface ProductEnableCardProps {
  product: Product;
  onChangeDefault: (isDefault: boolean) => void;
}

export function ProductCard({
  product,
  onChangeDefault,
}: ProductEnableCardProps) {
  const productDetails = getProductDetails(product);
  const { onboardingCopy, Icon } = productDetails;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDefaultHover, setIsDefaultHover] = useState(false);
  const [isDefaultTooltipOpen, setIsDefaultTooltipOpen] = useState(false);
  const history = useHistory();

  useEffect(() => {
    let id: number;
    if (isDefaultHover) {
      setIsDefaultTooltipOpen(true);
    } else {
      id = window.setTimeout(() => {
        setIsDefaultTooltipOpen(false);
      }, 250);
    }

    return () => window.clearTimeout(id);
  }, [isDefaultHover]);

  const handleShowTooltip = () => {
    setIsDefaultHover(true);
  };
  const handleHideTooltip = () => {
    setIsDefaultHover(false);
  };

  return (
    <Card
      size="small"
      className={styles.productEnableCard}
      data-testid={`product-${product.name}`}
    >
      <div className={styles.iconRow}>
        <Icon />
        {product.status == "Enabled" && (
          <Popover
            isOpen={isPopoverOpen}
            content={
              <Menu>
                {product.hasSettings && (
                  <Menu.Item
                    label={<strong>Settings</strong>}
                    subtext="Configure the settings for this product"
                    id="settings"
                    data-testid="settings"
                    onClick={() => {
                      history.push(`/settings/products/${product.key}`);
                    }}
                  />
                )}
                <Menu.Item
                  label={
                    <strong>
                      {(product.isDefault ? "Remove from" : "Add to") +
                        " defaults"}
                    </strong>
                  }
                  subtext={
                    product.isDefault
                      ? "This product will no longer automatically be used with new companies"
                      : "This product will be automatically used with new companies"
                  }
                  id="toggle-defaults"
                  data-testid="toggle-defaults"
                  onClick={() => {
                    onChangeDefault?.(!product.isDefault);
                    setIsPopoverOpen(false);
                  }}
                />
              </Menu>
            }
            onClose={() => setIsPopoverOpen(false)}
            placement="bottom-end"
            removePadding
          >
            <Button
              label="Actions"
              variant="tertiary"
              color="interface"
              onClick={() => setIsPopoverOpen(!isPopoverOpen)}
              data-testid="actions-button"
              className={styles.menuButton}
            >
              <KebabIcon />
            </Button>
          </Popover>
        )}
      </div>
      <div className={styles.textWrapper}>
        <Typography as="h3" variant="heading_md">
          {product.name}
        </Typography>
        {product.isDefault && (
          <Tooltip
            isOpen={isDefaultTooltipOpen}
            content={
              <TooltipContent>
                Default products will be automatically assigned to new companies
                unless otherwise specified.
              </TooltipContent>
            }
          >
            <button
              className={styles.default}
              onMouseEnter={handleShowTooltip}
              onMouseLeave={handleHideTooltip}
              onFocus={handleShowTooltip}
              onBlur={handleHideTooltip}
            >
              <IndicatorLabel
                label="Default"
                color="interface"
                data-testId="default-indicator"
              />
            </button>
          </Tooltip>
        )}
        <Typography as="p" variant="body_sm">
          {onboardingCopy}
        </Typography>
      </div>
    </Card>
  );
}
